import React, { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useDispatch, useSelector } from 'react-redux'
import { login } from '@/state/auth/auth.slice'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeSlash, Envelope, Lock } from '@phosphor-icons/react'
import { ShieldCheck, TrendingUp, Globe, AlertCircle } from 'lucide-react'
import { motion } from 'framer-motion'

const formSchema = z.object({
  email: z.string().email({
    message: 'Please enter a valid email address.',
  }),
  password: z.string().min(8, {
    message: 'Password must be at least 8 characters.',
  }),
})

export default function LoginPage() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const { error, success, user, isLoading: reduxIsLoading } = useSelector((state) => state.auth)

  useEffect(() => {
    if (error) {
      setIsLoading(false)
    }
  }, [error])

  useEffect(() => {
    if (success && user) {
      navigate('/dashboard')
    }
  }, [success, user, navigate])

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  function onSubmit(values) {
    setIsLoading(true)
    dispatch(login(values))
  }

  return (
    <div className="flex min-h-screen bg-background text-foreground font-sans">
      {/* Left Column - Branding Panel */}
      <div className="hidden lg:flex w-1/2 bg-secondary relative overflow-hidden flex-col justify-between p-12 lg:p-16">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary/20 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-primary/10 translate-x-1/3 translate-y-1/3 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-primary/5 -translate-x-1/2 -translate-y-1/2 rotate-45" />
        </div>

        {/* Logo */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="relative z-10"
        >
          <img 
            src="/assets/brand/logo-black.png" 
            className="w-auto h-12 md:h-16"
            alt="CetaPOS Logo"
          />
        </motion.div>

        {/* Main Content */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
          className="relative z-10 space-y-10"
        >
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-secondary-foreground leading-tight tracking-tight">
              Enterprise Point
              <br />
              of Sale System
            </h1>
            <p className="mt-6 text-lg text-secondary-foreground/80 max-w-md leading-relaxed">
              Streamline your business operations with our comprehensive inventory management and sales tracking platform.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-5">
            {[
              { icon: ShieldCheck, text: 'Enterprise-grade security' },
              { icon: TrendingUp, text: 'Real-time inventory tracking' },
              { icon: Globe, text: 'Multi-location support' }
            ].map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 + (index * 0.1) }}
                className="flex items-center gap-4 text-secondary-foreground/90 group"
              >
                <div className="h-10 w-10 bg-secondary-foreground/5 rounded-full flex items-center justify-center transition-transform group-hover:scale-110 duration-300 border border-secondary-foreground/10">
                  <feature.icon className="h-5 w-5" />
                </div>
                <span className="text-base font-medium">{feature.text}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="relative z-10 text-sm text-secondary-foreground/60 font-medium"
        >
          <p>Trusted by businesses worldwide</p>
        </motion.div>
      </div>

      {/* Right Column - Login Form */}
      <div className="w-full lg:w-1/2 bg-background flex flex-col justify-center items-center px-8 sm:px-12 lg:px-16 relative">
        {/* Decorative ambient gradient */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[100px]" />
          <div className="absolute top-[40%] left-[10%] w-[30%] h-[30%] rounded-full bg-secondary/20 blur-[80px]" />
        </div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-[400px] w-full mx-auto space-y-8 relative z-10"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <img 
              src="/assets/brand/logo-black.png" 
              className="w-auto h-12"
              alt="CetaPOS Logo"
            />
          </div>

          {/* Header */}
          <div className="text-center lg:text-left space-y-2">
            <h2 className="text-3xl font-bold text-foreground tracking-tight">
              Welcome back
            </h2>
            <p className="text-muted-foreground">
              Enter your credentials to access your dashboard
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 p-4 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg text-sm font-medium"
            >
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-foreground text-sm font-semibold">
                      Email address
                    </FormLabel>
                    <FormControl>
                      <div className="relative group">
                        <Envelope className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" weight="regular" />
                        <Input
                          placeholder="name@company.com"
                          {...field}
                          className="h-12 pl-11 bg-muted/30 border-input text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-primary/20 transition-all rounded-lg"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-foreground text-sm font-semibold">
                      Password
                    </FormLabel>
                    <FormControl>
                      <div className="relative group">
                        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" weight="regular" />
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          {...field}
                          className="h-12 pl-11 pr-11 bg-muted/30 border-input text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-primary/20 transition-all rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                          tabIndex={-1}
                        >
                          {showPassword ? (
                            <EyeSlash weight="regular" className="h-5 w-5" />
                          ) : (
                            <Eye weight="regular" className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-input text-primary focus:ring-primary cursor-pointer"
                  />
                  <span className="text-muted-foreground group-hover:text-foreground transition-colors">Remember me</span>
                </label>
                <a href="#" className="text-primary hover:text-primary/80 font-semibold transition-colors">
                  Forgot password?
                </a>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-base shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all rounded-lg"
                disabled={isLoading || reduxIsLoading}
              >
                {isLoading || reduxIsLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground animate-spin rounded-full" />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>
          </Form>

          {/* Footer */}
          <div className="pt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Don&apos;t have an account?{' '}
              <a href="https://cetapos.com/contact" target="_blank" rel="noreferrer" className="text-primary hover:text-primary/80 font-semibold transition-colors">
                Contact Sales
              </a>
            </p>
            <p className="mt-8 text-xs text-muted-foreground/60">
              © {new Date().getFullYear()} CetaPOS. All rights reserved.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
